/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useRecoilState } from "recoil";
import { protectedCategories } from "../../api/Categories";
import { LinksAPI } from "../../api/Links";
import { LinksAtom } from "../../atoms/LinksAtom";
import { ICategoryType } from "../../generated/Api";
import { ILink } from "../../types/LinkTypes";
import { ITag } from "../../types/TagTypes";
import StyledButton from "../StyledButton";
import AutoCompleteInterestTag, {
  ILinkOption,
} from "./AutoCompleteInterestTag";
import InterestTagName from "./InterestTagName";

const NoResultsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fff;
  color: #000;
  text-align: center;
  padding-bottom: 19px;
`;

const CreateTagButton = styled(StyledButton)`
  margin-top: 12px;
`;

const NewTagContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 18px 0 0 18px;
  background-color: #ffffff;
  color: #000;
`;

const Divider = styled.div`
  border-top: 1px solid #eaeaea;
  margin: 15px 0px 15px -30px;
`;

export const SystemTag = styled.div`
  font-size: 11px;
  background-color: #eceeef;
  color: #9d9e9e;
  margin-left: 10px;
  width: 50px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const GroupContainer = styled.div`
  display: flex;
  align-items: center;
  background-color: #fff;
  :hover {
    background-color: #fff;
  }
`;

const GroupName = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  line-height: 26px;
  letter-spacing: 0;
  font-weight: 600;
  color: #000;
  background-color: #fff;
`;

const NewTagContent = styled.div`
  padding: 15px;
`;

const Cancel = styled.span`
  width: 70px;
  height: 30px;

  position: absolute;
  cursor: pointer;

  right: 10px;
  top: 18px;

  align-items: center;
  justify-content: center;

  display: flex;
  background-color: #fff2e5;
  border-radius: 4px;
  border: 1px solid #fb972e;
  font-size: 16px !important;

  .https-text {
    color: #fb972e;

    font-size: 16px !important;
    letter-spacing: 0;
    font-weight: 600;
  }
`;

interface Props {
  interestTagging?: boolean;
  links: ILink[];
  placeholder?: string;
  disabled?: boolean;
  createOnly?: boolean;
  handleChoseLinks: (links: ILink[]) => void;
  tagInputValue: string;
  setTagInputValue: (value: string) => any;
  linkInputRef?: React.MutableRefObject<HTMLInputElement>;
}

export default function InterestTagSelect(props: Props): JSX.Element {
  const {
    linkInputRef,
    links,
    disabled,
    handleChoseLinks,
    placeholder,
    interestTagging,
    createOnly,
    tagInputValue,
    setTagInputValue,
  } = props;
  const [linkSuggestions, setLinkSuggestions] = useState<ILinkOption[]>();
  const [goToCategoryDropdown, setGoToCategoryDropdown] = useState(false);
  const [categoriesSuggestions, setCategoriesSuggestions] = useState<
    Array<{
      value: string;
      name: string;
      categoryType: ICategoryType;
    }>
  >();
  const [categoryInputValue, setCategoryInputValue] = useState("");
  const [linksPool, setLinksPool] = useRecoilState(LinksAtom);
  const [loading, setLoading] = useState(false);
  const [isHttps, setIsHttps] = useState(true);

  useEffect(() => {
    LinksAPI.readAll().then((response) => {
      setLinksPool(response);
    });
  }, []);

  useEffect(() => {
    if (!linksPool) return;

    let sortedTagNames = linksPool.slice().sort((a, b) => {
      let isSameCategory = a.category.localeCompare(b.category, undefined, {
        sensitivity: "base",
      });
      if (isSameCategory === 0) {
        return a.name.localeCompare(b.name, undefined, {
          sensitivity: "base",
        });
      } else {
        return isSameCategory;
      }
    });

    setLinkSuggestions(sortedTagNames);
  }, [linksPool]);

  useEffect(() => {
    if (!linksPool) return;

    const uniqueCategories = Array.from(
      new Set(linksPool.map((t) => t.category))
    );

    const uniqueCategoriesObj = uniqueCategories.map((category) => ({
      value: linksPool.find((link) => link.category === category)?._id!,
      name: category || "",
      categoryType: linksPool.find((link) => link.category === category)?.type!,
    }));

    setCategoriesSuggestions(uniqueCategoriesObj);
  }, [linksPool]);

  function handleTagValueSelect({ _id, name }: ITag) {
    if (name !== "" && linksPool) {
      const alreadyIn = links.find((link) => link._id === _id);
      const tagExists = linksPool.find((link) => link._id === _id);

      if (!alreadyIn && tagExists) {
        handleChoseLinks([tagExists]);
      }
    }
  }

  function deleteTag(elem: ITag) {
    handleChoseLinks(links.filter((link) => link._id !== elem._id));
  }

  async function handleCategoryValueSelect(categoryName: string) {
    if (loading) return;

    setLoading(true);

    const isProtected = protectedCategories.find(
      (c) =>
        c.toLowerCase().replace(/\s\s+/g, " ").trim() ===
        categoryName.toLowerCase().replace(/\s\s+/g, " ").trim()
    );

    if (isProtected) {
      toast("Can't create category with system category name", {
        type: "info",
      });
    } else if (tagInputValue && categoryName) {
      const tagResponse = await LinksAPI.create({
        url:
          (isHttps ? "https://" : "http://") +
          tagInputValue
            .replace(/^https?:\/\//gi, "")
            .replace(/^h‌ttps?:\/\//gi, "")
            // Edge case - Copied from unlinked links
            .replace(/\s\s+/g, " ")
            .trim(),
        name: categoryName.replace(/\s\s+/g, " ").trim(),
        externalId: null,
      });

      if (tagResponse) {
        const newLink = {
          _id: tagResponse._id?.toString() || "",
          name: tagResponse.name,
          userUid: tagResponse.userUid,
          createdAt: tagResponse.createdAt,
          categoryId: tagResponse.categoryId.toString(),
          category: "interest",
          type: ICategoryType.System,
          url: tagResponse.url,
        };
        // @ts-ignore
        setLinksPool((links) => [...links, newLink]);

        toast(`${categoryName} -> ${tagInputValue} link succesfully created!`, {
          type: "success",
        });

        setGoToCategoryDropdown(false);
        setTagInputValue("");
        setCategoryInputValue("");

        // @ts-ignore
        handleChoseLinks([newLink]);
      }
    } else {
      if (categoryName) {
        toast("Tag name can't be empty!", { type: "error" });
      } else {
        toast("Category name can't be empty!", { type: "error" });
      }
    }
    setLoading(false);
  }

  const NewTagHeader = () => {
    return (
      <NewTagContainer>
        <Cancel
          onClick={() => {
            setIsHttps(!isHttps);
          }}
        >
          <div className="https-text">{isHttps ? "https" : "http"}</div>
        </Cancel>
        <GroupContainer>
          <GroupName>New interest tag</GroupName>
          <SystemTag>SYSTEM</SystemTag>
        </GroupContainer>
        <NewTagContent>
          {categoryInputValue} ({isHttps ? "https://" : "http://"}
          {tagInputValue
            .replace(/^https?:\/\//gi, "")
            .replace(/^h‌ttps?:\/\//gi, "")}
          )
        </NewTagContent>
        <Divider />
      </NewTagContainer>
    );
  };

  const NoCategoriesFound = () => {
    return (
      <NoResultsContainer>
        <CreateTagButton
          className={"link"}
          isLoading={loading}
          disable={categoryInputValue === ""}
          onClick={() => handleCategoryValueSelect(categoryInputValue)}
        >
          Create interest tag
        </CreateTagButton>
      </NoResultsContainer>
    );
  };

  function viewCategoryDropdown() {
    setGoToCategoryDropdown(true);
  }

  return (
    <div
      css={css`
        width: 100%;
      `}
    >
      <div
        style={{
          pointerEvents: disabled ? "none" : "auto",
        }}
        css={css`
          width: 100%;
        `}
      >
        {!goToCategoryDropdown ? (
          <AutoCompleteInterestTag
            linkInputRef={linkInputRef}
            absolutePosition={interestTagging}
            selectedValue={tagInputValue}
            onValueSelect={handleTagValueSelect}
            onEnter={handleTagValueSelect}
            onValueChange={setTagInputValue}
            placeholder={placeholder || "Search for links"}
            createOnly={createOnly}
            links={links}
            deleteTag={deleteTag}
            disabled={disabled}
            viewCategories={viewCategoryDropdown}
          />
        ) : (
          <InterestTagName
            absolutePosition={interestTagging}
            tagValue={tagInputValue}
            selectedValue={categoryInputValue}
            linkSuggestions={linkSuggestions}
            onValueSelect={(option) => handleCategoryValueSelect(option.name)}
            onEnter={(option) => handleCategoryValueSelect(option.name)}
            onValueChange={setCategoryInputValue}
            placeholder="Enter a name for this interest tag"
            suggestions={categoriesSuggestions}
            dropdownHeader={NewTagHeader()}
            createOnly={createOnly}
            noResultsComponent={NoCategoriesFound()}
            handleCategoryValueSelect={handleCategoryValueSelect}
            onCancel={() => {
              setGoToCategoryDropdown(false);
              setTagInputValue("");
              setCategoryInputValue("");
            }}
            isCategoryComponent
          />
        )}
      </div>
    </div>
  );
}
